import isSpacedString from "@/helpers/isSpacedString";
import recursivelyToCamel from "@/helpers/recursivelyToCamel";
import type { MapboxSearchSuggestResponse } from "@/interfaces/mapboxSearchSuggestResponse";
import type { MapboxSessionData } from "@/interfaces/mapboxSessionData";
import { useMapboxSearchStore } from "@/stores/mapboxSearch";
import { useQuery } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { MaybeRef, isRef, watch } from "vue";
import mapboxApi from "../api/mapboxApi";

interface Props {
  params?: Omit<SearchParams, "q">;
}

interface SearchParams {
  q: string;
  language?: string;
  proximity?: string;
  limit?: number;
  country?: MaybeRef<string>;
}

const access_token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const getSearchSuggest = async (
  params: SearchParams,
  sessionData: MapboxSessionData,
): Promise<MapboxSearchSuggestResponse | void> => {
  try {
    const { language = "es", proximity = "ip", limit = 10 } = params;
    const {
      sessionToken,
      suggestUsedTimes,
      expiresAt,
      addSuggestUsedTimes,
      resetApiValues,
    } = sessionData;

    if (
      !sessionToken ||
      suggestUsedTimes === 50 ||
      (expiresAt && expiresAt < new Date())
    )
      resetApiValues(true);

    const { data } = await mapboxApi.get<MapboxSearchSuggestResponse>(
      "/search/searchbox/v1/suggest",
      {
        params: {
          access_token,
          session_token: sessionToken,
          language,
          proximity,
          limit,
          ...params,
          types: "postcode,place,city,neighborhood,poi",
          poi_category_exclusions: "",
          country: isRef(params.country)
            ? params.country.value
            : params.country,
        },
      },
    );

    const draft = recursivelyToCamel(data) as MapboxSearchSuggestResponse;

    const filteredSuggestions = draft.suggestions.filter((suggestion) => {
      return suggestion.featureType === "poi";
    });

    addSuggestUsedTimes();
    return {
      ...draft,
      suggestions: draft.suggestions.filter(
        (suggestion) => suggestion.featureType !== "poi",
      ),
      suggestionByPoi: filteredSuggestions,
    };
  } catch (err) {
    // Error occurred
  }
};

const useSearchBoxSuggest = (props: Props = {}) => {
  const {
    setSearchResults,
    setSearchQuery,
    setSelectedPlace,
    setNewSessionToken,
    addSuggestUsedTimes,
    resetApiValues,
    resetSessionToken,
    resetSuggestUsedTimes,
    removeSelectedPlace,
    setSuggestedPlacesByPoi,
    resetSuggestedPlacesByPoi,
  } = useMapboxSearchStore();
  const {
    searchQuery,
    searchResults,
    selectedPlace,
    selectedPlaceId,
    expiresAt,
    suggestedPlaces,
    sessionToken,
    suggestUsedTimes,
    suggestedPoi,
    suggestedPlacesByPoi,
  } = storeToRefs(useMapboxSearchStore());

  const { data, error, refetch, isFetching } = useQuery(
    ["searchedBoxSuggest"],
    () =>
      getSearchSuggest(
        { q: searchQuery.value, ...props.params },
        {
          sessionToken: sessionToken.value,
          suggestUsedTimes: suggestUsedTimes.value,
          expiresAt: expiresAt.value,
          setNewSessionToken,
          addSuggestUsedTimes,
          resetApiValues,
          resetSessionToken,
          resetSuggestUsedTimes,
        },
      ),
    {
      retry: 3,
      enabled: false,
    },
  );

  const search = useDebounceFn(async () => {
    if (searchQuery.value && !isSpacedString(searchQuery.value)) {
      refetch();
    }
  }, 100);

  watch(data, (value) => {
    if (value) {
      setSearchResults(value);
      setSuggestedPlacesByPoi(value.suggestionByPoi);
    }
  });

  watch(searchQuery, (value) => {
    value ? search() : setSearchResults(undefined);
  });

  return {
    searchQuery,
    searchResults,
    selectedPlace,
    suggestedPlaces,
    suggestedPoi,
    suggestedPlacesByPoi,
    resetSuggestedPlacesByPoi,
    selectedPlaceId,

    isFetching,
    error,

    search,
    setSearchQuery,
    setSelectedPlace,
    removeSelectedPlace,
  };
};

export default useSearchBoxSuggest;
